import {
  CoBuilderDocument,
  CoBuilderResult,
  GatewayTarget,
  GatewayTool,
  WizardDelivery,
  WizardInput,
} from '../services/agent-builder.interface';
import { parseScheduleExpression } from './schedule-parse';
import { deriveReportTemplate, reportSkeletonMarkdown } from './report-derive';
import { deliveryWritesReport, reportConfigIsEmpty, starterReportConfig } from './report-delivery';
import { knowledgeBaseGranted } from '../data/ability-catalog';

/**
 * Deterministic co-builder used by the mock service (and as documentation of
 * the contract the real LLM endpoint honors): parse a plain-English edit
 * request into {reply, patch, section}. Patches mirror the server's shape —
 * sections arrive fully merged, tools resolved to real catalog entries, and
 * shape flags derived from the resolved set.
 */

const TONE_LINES: Array<[RegExp, string]> = [
  [/friendlier|friendly|warmer|warm tone/, 'Keep a warm, friendly tone — write like a helpful colleague.'],
  [/concise|shorter|briefer|less wordy|brief/, 'Keep answers brief — three sentences or fewer unless asked for detail.'],
  [/formal|professional/, 'Maintain a formal, professional tone.'],
  [/simpler|plain language|less technical/, 'Use plain language — avoid jargon and acronyms.'],
];

const HELP =
  'I can edit this agent for you. Try things like:\n' +
  '• “add web search”\n' +
  '• “make it friendlier” or “more concise”\n' +
  '• “it shouldn\'t discuss pricing”\n' +
  '• “rename it to Deal Coach”\n' +
  '• “make it available to everyone”\n' +
  '• “set keywords to renewals, licensing, quotes”\n' +
  '• “what\'s missing?”';

interface CatalogHit {
  target: string;
  tool: GatewayTool;
  /** Name persisted in WizardInput.tools — the un-prefixed operationId the
   *  model calls (same rule as the Abilities picker's wireName). */
  wire: string;
}

const wireName = (t: GatewayTool): string => t.operationId || t.name;

/** Generic words that add no routing value — mirrors the backend's
 *  keyword_gen.sanitize_keywords stoplist (never let the router match on
 *  filler that every agent could plausibly claim). */
const GENERIC_KEYWORDS = new Set(['agent', 'watchguard', 'assistant', 'bot', 'help', 'tool']);

/** Lowercase, dedupe, strip generic terms and cap — same shape as the
 *  server's identity.keywords sanitiser, so the mock and the real backend
 *  behave the same way when a creator edits keywords through the co-builder. */
function sanitizeKeywords(words: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of words) {
    const k = raw.trim().toLowerCase().replace(/^["“'”]+|["“'”]+$/g, '');
    if (!k || GENERIC_KEYWORDS.has(k) || seen.has(k)) continue;
    seen.add(k);
    out.push(k.slice(0, 40));
    if (out.length >= 10) break;
  }
  return out;
}

/** Splits a comma/"and"-separated phrase into individual keyword candidates. */
function splitKeywordList(text: string): string[] {
  return text
    .split(/,|\band\b/i)
    .map((w) => w.trim())
    .filter(Boolean);
}

/** Loose match: full candidate phrase contained in the message, or ≥2
 *  significant words overlapping. */
function findTool(lower: string, catalog: GatewayTarget[]): CatalogHit | null {
  let best: { hit: CatalogHit; score: number } | null = null;
  for (const entry of catalog) {
    for (const tool of entry.tools) {
      const candidates = [
        wireName(tool).replace(/_/g, ' ').toLowerCase(),
        tool.name.replace(/_/g, ' ').toLowerCase(),
        (tool.displayName || '').toLowerCase(),
        (tool.friendly?.name || '').toLowerCase(),
      ].filter(Boolean);
      let score = 0;
      for (const c of candidates) {
        if (lower.includes(c)) score = Math.max(score, 100);
        else {
          const words = c.split(/\s+/).filter((w) => w.length > 3);
          const overlap = words.filter((w) => lower.includes(w)).length;
          if (overlap >= 2 || (overlap === 1 && words.length === 1)) score = Math.max(score, overlap);
        }
      }
      if (score > 0 && (!best || score > best.score)) {
        best = { hit: { target: entry.target, tool, wire: wireName(tool) }, score };
      }
    }
  }
  return best?.hit ?? null;
}

function mergedSection<K extends 'identity' | 'prompt' | 'meta' | 'shape'>(
  inputs: WizardInput,
  key: K,
  changes: Partial<WizardInput[K]>,
): WizardInput[K] {
  return { ...inputs[key], ...changes };
}

const EMAIL_RE = /[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}/gi;
/** Extracts an explicit subject from phrases like: with subject "Foo", subject line 'Bar', subject: Baz */
const SUBJECT_RE = /(?:with\s+)?subject(?:\s+line)?[:\s]+["']?([^"'\n,]+?)["']?(?:[,.]|$)/i;
/** A Microsoft Teams Incoming-Webhook URL. */
const TEAMS_WEBHOOK_RE = /https:\/\/[a-z0-9.\-]*webhook\.office\.com\/\S+/i;

/** Explicit request to make the agent interactive again (automation → chat).
 *  The trigger fields stay in the shape, inert — switching back restores the
 *  previous schedule (server parity: _sanitize_schedule). */
const TO_CHAT_PHRASE =
  /\b(make|turn)\b.*\b(chat|interactive|conversational)\b.*\bagent\b|\bmake (it|this) (a )?chat agent\b|\b(take|turn) (it|this) off the schedule\b|\b(remove|drop|stop) the schedule\b|\bi'?d rather (just )?(ask|chat)\b/i;

/** Explicit request to make the agent run on its own (chat → automation) with
 *  no cadence named — a cadence phrase routes through the schedule branch,
 *  which also switches the kind for chat drafts. */
const TO_AUTOMATION_PHRASE =
  /\b(make|turn|convert)\b.*\b(scheduled|automation)\b.*\bagent\b|\bmake (it|this) scheduled\b|\brun(s)? on its own\b|\bput (it|this) on a schedule\b/i;

/** The uploaded-documents knowledge base (shape.has_knowledge_base) — the
 *  creator uploads Markdown/CSV files the agent answers from. Distinct from
 *  has_file_upload (an end user attaching one file in chat). */
const KNOWLEDGE_BASE_PHRASE =
  /\bknowledge base\b|\banswer(s)? from (my|our|the) (uploaded )?(docs|documents|files|polic(y|ies))\b|\b(docs|documents|files) (i|we)('ll| will)? upload\b/i;

/** Plain-English phrases that name a cadence or a delivery change — the
 *  trigger for the schedule branch. Kept loose, like the other branches. */
const SCHEDULE_PHRASE =
  /\b(run|runs|fire|trigger)\b.*\b(every|each|daily|weekly|monthly|hourly|nightly)\b|\b(every|each)\s+(hour|day|week|month|morning|evening|night|weekday|monday|tuesday|wednesday|thursday|friday|saturday|sunday)|\b(daily|weekly|monthly|hourly|nightly)\b|\bevery\s+\d+\s+(minute|hour|day|week)s?\b|\bon a schedule\b|\bschedule it\b|\bcron\b|\brate\(/i;

const DELIVERY_PHRASE =
  /\b(email|e-mail|mail|send)\b.*\b(result|results|report|it|to|recap|summary|digest)\b|\bsave (it|the result|reports?)\b|\b(to the|in the)?\s*reports? bucket\b|\bdeliver\b|\b(post|send|drop)\b.*\bteams\b|\bteams (channel|webhook)\b/i;

/** "When <X> happens / is created / changes" — a domain-event trigger. The
 *  named event becomes (optional) context for the prompt; we don't try to wire
 *  a concrete EventBridge source here (the server owns that). Excludes "when a
 *  file arrives/lands" which is the s3 trigger below. */
const EVENT_TRIGGER_PHRASE =
  /\bwhen\s+(?:a|an|the|some|new)\b(?!.*\b(file|files|object|document|report|csv|pdf|upload)\b).*\b(is|are|gets?|happens?|created|added|updated|changed|closed|opened|submitted|received)\b/i;

/** "When a file lands/arrives in <folder>" — an s3 (file-arrival) trigger. */
const S3_TRIGGER_PHRASE =
  /\bwhen\b.*\b(file|files|object|document|report|csv|pdf|upload)s?\b.*\b(arrives?|lands?|appears?|is\s+(uploaded|added|dropped|created)|shows up)\b|\b(file|files)\b.*\b(arrives?|lands?)\b/i;

/** Pull a folder/prefix out of "when a file lands in the reports folder",
 *  "...in reports/inbound", "...the uploads/ directory". Normalizes to a
 *  trailing-slash key prefix. Returns null when no folder is named. */
function parseS3Prefix(text: string): string | null {
  const lower = text.toLowerCase();
  // An explicit path-like token ("reports/inbound", "uploads/").
  const path = lower.match(/\b([a-z0-9][a-z0-9._\-]*(?:\/[a-z0-9._\-]+)+\/?)/);
  if (path) return path[1].endsWith('/') ? path[1] : path[1] + '/';
  // "...in the <name> folder/directory/bucket".
  const named = lower.match(/\b(?:in|to|under|inside)\s+(?:the\s+)?["“']?([a-z0-9][a-z0-9._\-]*)["”']?\s+(?:folder|directory|dir|bucket|prefix)\b/);
  if (named) return named[1] + '/';
  // "...the <name> folder".
  const folderFirst = lower.match(/\b(?:the\s+)?["“']?([a-z0-9][a-z0-9._\-]*)["”']?\s+folder\b/);
  if (folderFirst) return folderFirst[1] + '/';
  return null;
}

/** Server parity (_sanitize_patch): an automation's downloadable report is on
 *  exactly when its delivery keeps an S3 report. Applies the flag to `changes`
 *  and, when the report turns on with no template yet, returns the meta FIELDS
 *  seeding the starter outline (and pushes an explanatory note) — the caller
 *  merges them so they compose with other meta edits (schedule_unconfirmed). */
function coupleReportToDelivery(
  inputs: WizardInput,
  changes: Partial<WizardInput['shape']>,
  notes: string[],
): Partial<WizardInput['meta']> {
  const kind = changes.delivery?.kind ?? inputs.shape.delivery?.kind ?? 's3';
  const wantsReport = deliveryWritesReport(kind);
  changes.has_report_artifact = wantsReport;
  if (wantsReport && reportConfigIsEmpty(inputs.meta.report)) {
    notes.push(
      'I started a report outline (Summary, Key findings, Details) — edit it in the **Report preview** tab',
    );
    return { report: { ...(inputs.meta.report ?? {}), ...starterReportConfig() } };
  }
  return {};
}

function toolsPatch(inputs: WizardInput, tools: WizardInput['tools'], catalog: GatewayTarget[]) {
  // Targets may mix — each tool routes itself; gateway_target stays as the
  // fallback for entries without one (mirrors the server's _resolve_tools).
  const targets = new Set<string>();
  let hasUnrouted = false;
  for (const t of tools) {
    const target = t.target
      ?? catalog.find((entry) => entry.tools.some((ct) => wireName(ct) === t.name))?.target;
    if (target) targets.add(target);
    else hasUnrouted = true;
  }
  let gatewayTarget = inputs.shape.gateway_target ?? null;
  if (tools.length === 0) {
    gatewayTarget = null;
  } else if (hasUnrouted && gatewayTarget) {
    // keep the fallback the unrouted entries rely on
  } else if (targets.size === 1) {
    gatewayTarget = [...targets][0];
  } else if (targets.size > 1 && (!gatewayTarget || !targets.has(gatewayTarget))) {
    gatewayTarget = [...targets].sort()[0];
  }
  return {
    tools,
    shape: {
      ...inputs.shape,
      has_tools: tools.length > 0,
      tools_via_gateway: targets.size > 0 || (tools.length > 0 && !!gatewayTarget),
      gateway_target: gatewayTarget,
    },
  };
}

export function coBuilderLocalParse(
  inputs: WizardInput,
  message: string,
  catalog: GatewayTarget[],
  document?: CoBuilderDocument,
  kbGranted?: boolean,
): CoBuilderResult {
  const raw = message.trim();
  const lower = raw.toLowerCase();

  // An attached document is a strong signal: derive a report template from it
  // (title + section outline). Also handles "build a report template from this"
  // with the doc attached.
  if (document && document.text.trim()) {
    const report = deriveReportTemplate(document, inputs.meta.report?.format === 'pdf' ? 'pdf' : 'html');
    const enabling = inputs.shape.has_report_artifact ? '' : ' I also turned on **Create downloadable reports**.';
    // Match the wizard's "Generate template": title + sections + a Markdown
    // skeleton. (The real backend also generates a CSS theme; mock has no AI, so
    // it keeps the built-in theme — empty css.)
    const markdown = reportSkeletonMarkdown({ title: report.title, sections: report.sections });
    return {
      reply:
        `Built a report template from **${document.name}**: titled “${report.title}” with ` +
        `${report.sections.length} section${report.sections.length === 1 ? '' : 's'} ` +
        `(${report.sections.map((s) => s.title).join(', ')}).${enabling} Preview it in the **Report preview** tab.`,
      patch: {
        shape: { ...inputs.shape, has_report_artifact: true },
        meta: mergedSection(inputs, 'meta', {
          report: {
            ...(inputs.meta.report ?? {}),
            title: report.title,
            sections: report.sections,
            format: report.format,
            markdown,
          },
        }),
      },
      section: 'capabilities',
    };
  }

  if (!raw) return { reply: HELP, patch: null, section: null };

  // Readiness / review questions — point at the coach, list the obvious gaps.
  if (/missing|ready|improve|good enough|better|review my/.test(lower)) {
    const gaps: string[] = [];
    if (!inputs.identity.display_name || !inputs.identity.description) gaps.push('Named & described');
    if (!inputs.prompt.role) gaps.push('Role written');
    if ((inputs.prompt.instructions || '').length < 60) gaps.push('Behavior guidance added');
    if (inputs.shape.has_tools && inputs.tools.length === 0) gaps.push('At least one ability equipped');
    if (gaps.length === 0) {
      return { reply: "It's in good shape — everything on my checklist is covered. Submit when you're happy with the test answers.", patch: null, section: null };
    }
    return { reply: `Still missing:\n${gaps.map((g) => '• ' + g).join('\n')}`, patch: null, section: null };
  }

  // Who can use it
  if (/who can (use|see|access)/.test(lower)) {
    const vis = inputs.meta.visibility ?? 'me';
    const map = { me: 'only you (while you test)', dept: `everyone in ${inputs.identity.department || 'your department'}`, everyone: 'all WatchGuard employees' };
    return { reply: `Right now: ${map[vis]}. Say “make it available to everyone” or “my department” to change that.`, patch: null, section: null };
  }

  // Keywords (routing keywords in the Identity section) — set/replace, add, or
  // remove. Kept loose like the other branches; a bare "keywords" mention with
  // no list/target falls through to a clarifying nudge rather than guessing.
  if (/\bkeywords?\b/.test(lower)) {
    const current = inputs.identity.keywords ?? [];

    const setMatch = raw.match(/keywords?\s*(?:to|as|:)\s*(.+)/i);
    if (setMatch) {
      const next = sanitizeKeywords(splitKeywordList(setMatch[1]));
      return {
        reply: next.length
          ? `Updated the keywords to: ${next.join(', ')}.`
          : "I couldn't pull a keyword list out of that — try “set keywords to renewals, licensing, quotes”.",
        patch: next.length ? { identity: mergedSection(inputs, 'identity', { keywords: next }) } : null,
        section: next.length ? 'identity' : null,
      };
    }

    if (/\badd\b|\binclude\b/.test(lower)) {
      const addMatch = raw.match(/(?:add|include)\s+(?:the\s+)?(?:keywords?\s+)?["“']?(.+?)["”']?(?:\s+(?:as\s+)?(?:a\s+)?keywords?)?$/i);
      const added = addMatch ? sanitizeKeywords(splitKeywordList(addMatch[1])) : [];
      const next = sanitizeKeywords([...current, ...added]);
      return {
        reply: added.length
          ? `Added **${added.join(', ')}** to the keywords — now: ${next.join(', ')}.`
          : "Tell me which word to add — e.g. “add renewals as a keyword”.",
        patch: added.length ? { identity: mergedSection(inputs, 'identity', { keywords: next }) } : null,
        section: added.length ? 'identity' : null,
      };
    }

    if (/\bremove\b|\bdrop\b|\bdelete\b/.test(lower)) {
      const removeMatch = raw.match(/(?:remove|drop|delete)\s+(?:the\s+)?(?:keywords?\s+)?["“']?(.+?)["”']?(?:\s+(?:as\s+)?(?:a\s+)?keywords?)?$/i);
      const targets = removeMatch ? splitKeywordList(removeMatch[1]).map((w) => w.toLowerCase()) : [];
      const next = current.filter((k) => !targets.includes(k.toLowerCase()));
      return {
        reply: targets.length && next.length !== current.length
          ? `Removed **${targets.join(', ')}** — now: ${next.length ? next.join(', ') : '(none)'}.`
          : "I couldn't match that to a current keyword — check the Identity section for what's set now.",
        patch: next.length !== current.length ? { identity: mergedSection(inputs, 'identity', { keywords: next }) } : null,
        section: next.length !== current.length ? 'identity' : null,
      };
    }

    return {
      reply: current.length
        ? `Current keywords: ${current.join(', ')}. Say “set keywords to …”, “add … as a keyword”, or “remove …” to change them.`
        : 'No keywords are set yet. Say “set keywords to renewals, licensing, quotes” to add some.',
      patch: null,
      section: null,
    };
  }

  // Rename
  const rn = raw.match(/(?:rename(?: it)?(?: to)?|call it|name it)\s+["“']?(.+?)["”']?$/i);
  if (rn) {
    const name = rn[1].replace(/\b\w/g, (c) => c.toUpperCase());
    return {
      reply: `Renamed to **${name}**.`,
      patch: { identity: mergedSection(inputs, 'identity', { display_name: name }) },
      section: 'identity',
    };
  }

  // Access
  if (/everyone|whole company|all employees|company-wide/.test(lower)) {
    return {
      reply: 'Done — when published, **everyone at WatchGuard** will be able to use it. That routes through admin approval.',
      patch: { meta: mergedSection(inputs, 'meta', { visibility: 'everyone' }) },
      section: 'access',
    };
  }
  if (/(my |the )?(department|team)\b/.test(lower) && /available|access|share|open|visib|make/.test(lower)) {
    return {
      reply: `Done — it'll be visible to **everyone in ${inputs.identity.department || 'your department'}**.`,
      patch: { meta: mergedSection(inputs, 'meta', { visibility: 'dept' }) },
      section: 'access',
    };
  }
  if (/just me|only me|private/.test(lower)) {
    return {
      reply: "Done — it's **private to you** while you iterate.",
      patch: { meta: mergedSection(inputs, 'meta', { visibility: 'me' }) },
      section: 'access',
    };
  }

  // Guardrails ("it shouldn't discuss compensation")
  const guard = raw.match(/(?:shouldn't|should not|don't let it|do not|never|avoid|stop)\s+(.+)/i);
  if (guard && !/tone/.test(lower)) {
    const rule = 'Never ' + guard[1].trim().replace(/\.$/, '') + '.';
    return {
      reply: `Added a guardrail: “${rule}”`,
      patch: { prompt: mergedSection(inputs, 'prompt', { instructions: ((inputs.prompt.instructions || '').trim() + '\n\n' + rule).trim() }) },
      section: 'instructions',
    };
  }

  // Tone
  for (const [re, line] of TONE_LINES) {
    if (re.test(lower)) {
      return {
        reply: `Updated the instructions: “${line}”`,
        patch: { prompt: mergedSection(inputs, 'prompt', { instructions: ((inputs.prompt.instructions || '').trim() + '\n\n' + line).trim() }) },
        section: 'instructions',
      };
    }
  }

  // Agent kind: automation → chat (explicit request). Server parity: the
  // trigger fields stay in the shape, inert — switching back restores them.
  if (TO_CHAT_PHRASE.test(lower)) {
    if ((inputs.shape.agent_kind ?? 'chat') !== 'automation') {
      return { reply: "It's already an interactive chat agent.", patch: null, section: null };
    }
    return {
      reply:
        'Done — this is now an **interactive chat agent**. The schedule and delivery no longer ' +
        'apply (I kept them saved in case you switch back). People use it from Agent Hub chat.',
      patch: { shape: mergedSection(inputs, 'shape', { agent_kind: 'chat' }) },
      section: 'schedule',
    };
  }

  // Agent kind: chat → automation, no cadence named (a cadence routes through
  // the schedule branch below, which also switches chat drafts). Seeds the
  // same defaults as the Automation starter template.
  if (TO_AUTOMATION_PHRASE.test(lower) && !SCHEDULE_PHRASE.test(lower)) {
    if ((inputs.shape.agent_kind ?? 'chat') === 'automation') {
      return {
        reply: 'It already runs on a schedule — tell me a new cadence or delivery to change how.',
        patch: null,
        section: null,
      };
    }
    const changes: Partial<WizardInput['shape']> = { agent_kind: 'automation' };
    const notes: string[] = [];
    if (!inputs.shape.schedule_expression) {
      changes.schedule_expression = 'rate(1 day)';
      notes.push('it runs **once a day** until you set a cadence');
    }
    if (!inputs.shape.delivery?.kind) changes.delivery = { kind: 's3' };
    const metaFields = coupleReportToDelivery(inputs, changes, notes);
    return {
      reply:
        'Done — this is now a **scheduled agent**: it runs on its own and delivers each ' +
        `run's result${notes.length ? ' (' + notes.join('; ') + ')' : ''}. Fine-tune the ` +
        'trigger and delivery in the **How it runs** section, and make sure the task says ' +
        'what each run should do.',
      patch: {
        shape: mergedSection(inputs, 'shape', changes),
        ...(Object.keys(metaFields).length
          ? { meta: mergedSection(inputs, 'meta', metaFields) }
          : {}),
      },
      section: 'schedule',
    };
  }

  // Knowledge base (uploaded documents the agent answers from) — a shape
  // flag, never a tools[] entry: the search tool is render-owned. Gated on
  // the same creator grant the picker/library enforce (knowledgeBaseGranted)
  // — without this check the co-builder would "turn on" a capability the
  // caller can't actually use, which then 403s as kb_not_granted at test-run.
  if (KNOWLEDGE_BASE_PHRASE.test(lower)) {
    if (!knowledgeBaseGranted(catalog, kbGranted)) {
      return {
        reply:
          "The knowledge base isn't available on your account, so I can't turn it on for this agent.",
        patch: null,
        section: null,
      };
    }
    if (inputs.shape.has_knowledge_base) {
      return {
        reply:
          'The knowledge base is already on — upload documents in the **Knowledge base** section.',
        patch: null,
        section: null,
      };
    }
    return {
      reply:
        'Done — it answers from **your uploaded documents** now. Upload them in the new ' +
        '**Knowledge base** section (Markdown or CSV files); the agent cites what it finds.',
      patch: { shape: mergedSection(inputs, 'shape', { has_knowledge_base: true }) },
      section: 'capabilities',
    };
  }

  // Trigger + delivery (automation agents — a cadence/delivery request on a
  // chat agent switches it to an automation, server parity). Mirrors the
  // server's 'schedule' section: a trigger phrase patches
  // shape.{trigger_type, schedule_expression, s3_prefix}; a delivery phrase
  // patches shape.delivery (email/s3/teams).
  // Several can appear in one message ("run it every Monday and post to Teams").
  //   - "every weekday at 8"            → trigger_type 'schedule' + cron
  //   - "when a new lead is created…"   → trigger_type 'event'
  //   - "when a file lands in reports…" → trigger_type 's3' + s3_prefix
  const wantsSchedule = SCHEDULE_PHRASE.test(lower);
  const wantsS3Trigger = S3_TRIGGER_PHRASE.test(lower);
  const wantsEventTrigger = !wantsS3Trigger && EVENT_TRIGGER_PHRASE.test(lower);
  const wantsDelivery = DELIVERY_PHRASE.test(lower);
  if (wantsSchedule || wantsS3Trigger || wantsEventTrigger || wantsDelivery) {
    const changes: Partial<WizardInput['shape']> = {};
    const notes: string[] = [];
    let needsConfirm = false;

    // ── Trigger: file-arrival (s3) wins over event when both could match. ──
    if (wantsS3Trigger) {
      const prefix = parseS3Prefix(raw);
      changes.trigger_type = 's3';
      if (prefix) changes.s3_prefix = prefix;
      notes.push(prefix
        ? `it runs **when a file arrives** in \`${prefix}\``
        : `it runs **when a file arrives** — set the folder to watch in the Schedule section`);
    } else if (wantsEventTrigger) {
      changes.trigger_type = 'event';
      notes.push('it runs **when that event happens** — describe the event in the task so the agent knows what to do');
    } else if (wantsSchedule) {
      const cadence = parseScheduleExpression(lower);
      if (cadence.parsed) {
        changes.trigger_type = 'schedule';
        changes.schedule_expression = cadence.expression;
        needsConfirm = true;
        notes.push(`it runs **${cadence.label}** (\`${cadence.expression}\`)`);
      }
    }

    if (wantsDelivery) {
      const emails = [...raw.matchAll(EMAIL_RE)].map((m) => m[0]);
      const webhook = raw.match(TEAMS_WEBHOOK_RE)?.[0];
      const subject = raw.match(SUBJECT_RE)?.[1]?.trim() || undefined;
      const wantsTeams = /\bteams\b/.test(lower) || !!webhook;
      const wantsEmail = /\b(email|e-mail|mail)\b/.test(lower) || emails.length > 0;
      // "report link", "save to reports", "reports bucket", bare "s3"
      const wantsS3 = /\breport\s*(link|bucket|url)\b|\bsave\b|\breports? bucket\b|\bs3\b/.test(lower);
      // Combination kinds take priority when both an S3 and a channel signal appear.
      if (wantsTeams && wantsS3) {
        changes.delivery = {
          ...(inputs.shape.delivery ?? {}),
          kind: 's3+teams',
          ...(webhook ? { teams_webhook: webhook } : {}),
        };
        notes.push(webhook
          ? `each run's report is **saved to the reports bucket** and **posted to your Teams channel**`
          : `each run's report is **saved to the reports bucket** and **posted to Teams** — paste the channel's webhook URL in the Schedule section`);
      } else if (wantsEmail && wantsS3) {
        const existing = inputs.shape.delivery ?? {};
        const delivery: WizardDelivery = {
          ...existing,
          kind: 's3+email',
          ...(emails.length ? { to: emails } : {}),
          ...(subject ? { subject } : {}),
        };
        changes.delivery = delivery;
        notes.push(emails.length
          ? `each run's report is **saved to the reports bucket** and **emailed with a link to ${emails.join(', ')}**`
          : `each run's report is **saved to the reports bucket** and **emailed with a link** — add the recipients in the Schedule section`);
      } else if (wantsTeams) {
        changes.delivery = {
          ...(inputs.shape.delivery ?? {}),
          kind: 'teams',
          ...(webhook ? { teams_webhook: webhook } : {}),
        };
        notes.push(webhook
          ? `each run's result is **posted to your Teams channel** (no report bucket)`
          : `each run's result is **posted to Teams** (no report bucket) — paste the channel's webhook URL in the Schedule section`);
      } else if (wantsEmail) {
        const existing = inputs.shape.delivery ?? {};
        const delivery: WizardDelivery = {
          ...existing,
          kind: 'email',
          ...(emails.length ? { to: emails } : {}),
          ...(subject ? { subject } : {}),
        };
        changes.delivery = delivery;
        notes.push(emails.length
          ? `each run's result is **emailed directly to ${emails.join(', ')}** (no report bucket)`
          : `each run's result is **emailed directly** (no report bucket) — add the recipients in the Schedule section`);
      } else if (wantsS3) {
        changes.delivery = { ...(inputs.shape.delivery ?? {}), kind: 's3' };
        notes.push("each run's report is **saved to the reports bucket** with a share link");
      }
    }

    if (Object.keys(changes).length > 0) {
      const wasChat = (inputs.shape.agent_kind ?? 'chat') !== 'automation';
      if (wasChat) {
        // Server parity: a cadence/delivery request on a chat agent IS the
        // chat → automation switch — seed whatever the message didn't name so
        // the draft is never a scheduled agent with nothing to run on.
        changes.agent_kind = 'automation';
        if ((changes.trigger_type ?? 'schedule') === 'schedule'
            && !changes.schedule_expression && !inputs.shape.schedule_expression) {
          changes.schedule_expression = 'rate(1 day)';
          notes.push('it runs **once a day** until you set a cadence');
        }
        if (!changes.delivery && !inputs.shape.delivery?.kind) {
          changes.delivery = { kind: 's3' };
          notes.push("each run's report is **saved to the reports bucket**");
        }
        notes.unshift('this is now a **scheduled agent** that runs on its own');
      }
      const metaFields: Partial<WizardInput['meta']> = {};
      // Surfacing a concrete cron is a guess the creator should confirm (UTC).
      if (needsConfirm) metaFields.schedule_unconfirmed = true;
      if ((changes.agent_kind ?? inputs.shape.agent_kind) === 'automation') {
        Object.assign(metaFields, coupleReportToDelivery(inputs, changes, notes));
      }
      return {
        reply:
          `Updated the schedule — ${notes.join('; ')}.` +
          (wasChat ? ' Make sure the task says what each run should do.' : '') +
          (needsConfirm ? '\n\nEventBridge times are **UTC** — double-check it in the Schedule section.' : ''),
        patch: {
          shape: mergedSection(inputs, 'shape', changes),
          ...(Object.keys(metaFields).length
            ? { meta: mergedSection(inputs, 'meta', metaFields) }
            : {}),
        },
        section: 'schedule',
      };
    }
    // Recognized the intent but couldn't pin a cadence/recipient — nudge.
    return {
      reply:
        'Tell me how it should run and where results go — e.g. “run it every Monday at 8am and email the results to ' +
        'sales-team@watchguard.com”, “when a file arrives in reports/inbound, summarize it”, or “post the result to ' +
        'our Teams channel”. You can also open the **Schedule** section to set it directly.',
      patch: null,
      section: null,
    };
  }

  // Add ability
  if (/add|enable|equip|give it|include|let it|it should be able/.test(lower)) {
    const hit = findTool(lower, catalog);
    if (hit) {
      if (inputs.tools.some((t) => t.name === hit.wire)) {
        return { reply: `**${hit.tool.displayName || hit.wire}** is already equipped.`, patch: null, section: null };
      }
      const tools = [
        ...inputs.tools,
        { name: hit.wire, description: hit.tool.description, input_schema: hit.tool.input_schema, target: hit.target },
      ];
      return {
        reply: `Equipped **${hit.tool.displayName || hit.tool.name}** — try it in the preview.`,
        patch: toolsPatch(inputs, tools, catalog),
        section: 'capabilities',
      };
    }
    return { reply: "I couldn't find that ability in the catalog. Open **Abilities** to browse everything available.", patch: null, section: null };
  }

  // Remove ability
  if (/remove|drop|disable|take away/.test(lower)) {
    const equipped = inputs.tools.find((t) => {
      const words = t.name.replace(/_/g, ' ').toLowerCase().split(/\s+/).filter((w) => w.length > 3);
      return words.some((w) => lower.includes(w));
    });
    if (equipped) {
      const tools = inputs.tools.filter((t) => t.name !== equipped.name);
      return {
        reply: `Removed **${equipped.name.replace(/_/g, ' ')}**.`,
        patch: toolsPatch(inputs, tools, catalog),
        section: 'capabilities',
      };
    }
    return { reply: "I couldn't match that to an equipped ability — check the Abilities section for what's currently equipped.", patch: null, section: null };
  }

  return { reply: HELP, patch: null, section: null };
}
